/**
 * discovery-page-editor.tsx
 *
 * Staff-only CMS editor for the ACCESS discovery page content.
 * Allows staff to edit EN and ES content and publish without a code deploy.
 *
 * Route: /cms/discovery-page (staff-gated via RequireAuth in app.tsx)
 * Auth: StaffPoolAuthGuard on PATCH /v1/cms/discovery-page in betty-core-api.
 *
 * No PII. Content is public-facing copy only.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { useEffect, useState, type JSX, type FormEvent } from 'react';

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

interface EnrollmentStep {
  stepNumber: number;
  title: string;
  description: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface LocaleContent {
  programOverview: string;
  supportedConditions: string[];
  benefitsList: string[];
  enrollmentSteps: EnrollmentStep[];
  faqItems: FaqItem[];
  contactEmail: string;
  providerDirectoryUrl: string | null;
}

interface DiscoveryPageContent {
  id: string;
  contentEn: LocaleContent;
  contentEs: LocaleContent;
  publishedAt: string;
  publishedBy?: string;
}

type ActiveTab = 'en' | 'es';

const EMPTY_LOCALE_CONTENT: LocaleContent = {
  programOverview: '',
  supportedConditions: [],
  benefitsList: [],
  enrollmentSteps: [],
  faqItems: [],
  contactEmail: 'access@nourishedrx.com',
  providerDirectoryUrl: null,
};

/**
 * DiscoveryPageEditor — staff-only CMS editor component.
 *
 * Renders EN and ES tab editors for all discovery page content fields.
 * Publish button calls PATCH /v1/cms/discovery-page (staff auth required).
 */
export function DiscoveryPageEditor(): JSX.Element {
  const auth = usePortalAuth();
  const [content, setContent] = useState<DiscoveryPageContent | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('en');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // JSON editor raw state (for enrollmentSteps and faqItems — complex arrays)
  const [enrollmentStepsJsonEn, setEnrollmentStepsJsonEn] = useState('');
  const [enrollmentStepsJsonEs, setEnrollmentStepsJsonEs] = useState('');
  const [faqItemsJsonEn, setFaqItemsJsonEn] = useState('');
  const [faqItemsJsonEs, setFaqItemsJsonEs] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    void fetch(`${SERVICES_BASE_URL}/v1/core/cms/discovery-page`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Fetch failed: ${String(res.status)}`);
        const data = (await res.json()) as DiscoveryPageContent;
        setContent(data);
        setEnrollmentStepsJsonEn(JSON.stringify(data.contentEn.enrollmentSteps, null, 2));
        setEnrollmentStepsJsonEs(JSON.stringify(data.contentEs.enrollmentSteps, null, 2));
        setFaqItemsJsonEn(JSON.stringify(data.contentEn.faqItems, null, 2));
        setFaqItemsJsonEs(JSON.stringify(data.contentEs.faqItems, null, 2));
        setIsLoading(false);
      })
      .catch(() => {
        // New/empty content
        const empty: DiscoveryPageContent = {
          id: 'singleton',
          contentEn: { ...EMPTY_LOCALE_CONTENT },
          contentEs: { ...EMPTY_LOCALE_CONTENT },
          publishedAt: '',
        };
        setContent(empty);
        setEnrollmentStepsJsonEn('[]');
        setEnrollmentStepsJsonEs('[]');
        setFaqItemsJsonEn('[]');
        setFaqItemsJsonEs('[]');
        setIsLoading(false);
      });
  }, []);

  function updateStringField(
    lang: ActiveTab,
    field: 'programOverview' | 'contactEmail',
    value: string,
  ): void {
    if (!content) return;
    const key = lang === 'en' ? 'contentEn' : 'contentEs';
    setContent({ ...content, [key]: { ...content[key], [field]: value } });
  }

  function updateProviderDirectoryUrl(lang: ActiveTab, value: string): void {
    if (!content) return;
    const key = lang === 'en' ? 'contentEn' : 'contentEs';
    // Send null when URL is cleared — never send empty string
    setContent({
      ...content,
      [key]: { ...content[key], providerDirectoryUrl: value.trim() || null },
    });
  }

  function updateStringArrayField(
    lang: ActiveTab,
    field: 'supportedConditions' | 'benefitsList',
    rawValue: string,
  ): void {
    if (!content) return;
    const key = lang === 'en' ? 'contentEn' : 'contentEs';
    // Split on newlines, trim each line, filter blanks
    const arr = rawValue
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    setContent({ ...content, [key]: { ...content[key], [field]: arr } });
  }

  /** Parse JSON for complex array fields; sets jsonError if invalid. */
  function handleEnrollmentStepsJson(lang: ActiveTab, raw: string): void {
    if (lang === 'en') setEnrollmentStepsJsonEn(raw);
    else setEnrollmentStepsJsonEs(raw);

    try {
      const parsed = JSON.parse(raw) as EnrollmentStep[];
      if (!Array.isArray(parsed)) throw new Error('Must be an array');
      setJsonError(null);
      if (!content) return;
      const key = lang === 'en' ? 'contentEn' : 'contentEs';
      setContent({ ...content, [key]: { ...content[key], enrollmentSteps: parsed } });
    } catch (err) {
      setJsonError(
        `Enrollment steps JSON error: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  function handleFaqItemsJson(lang: ActiveTab, raw: string): void {
    if (lang === 'en') setFaqItemsJsonEn(raw);
    else setFaqItemsJsonEs(raw);

    try {
      const parsed = JSON.parse(raw) as FaqItem[];
      if (!Array.isArray(parsed)) throw new Error('Must be an array');
      setJsonError(null);
      if (!content) return;
      const key = lang === 'en' ? 'contentEn' : 'contentEs';
      setContent({ ...content, [key]: { ...content[key], faqItems: parsed } });
    } catch (err) {
      setJsonError(`FAQ items JSON error: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async function handlePublish(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!content) return;
    if (jsonError) {
      setSaveError('Please fix JSON errors before publishing.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const accessToken = auth.user?.access_token;
      if (!accessToken) {
        throw new Error('Not authenticated — please sign in again.');
      }
      const res = await fetch(`${SERVICES_BASE_URL}/v1/core/cms/discovery-page`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          contentEn: content.contentEn,
          contentEs: content.contentEs,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Publish failed (${String(res.status)}): ${errorText}`);
      }

      const updated = (await res.json()) as DiscoveryPageContent;
      setContent(updated);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsSaving(false);
    }
  }

  const localeContent = content && activeTab === 'en' ? content.contentEn : content?.contentEs;
  const enrollmentStepsJson = activeTab === 'en' ? enrollmentStepsJsonEn : enrollmentStepsJsonEs;
  const faqItemsJson = activeTab === 'en' ? faqItemsJsonEn : faqItemsJsonEs;

  return (
    <main className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Discovery Page Content</h1>

      {isLoading ? (
        <p className="text-gray-500">Loading CMS content…</p>
      ) : (
        <form onSubmit={(e) => void handlePublish(e)}>
          {/* Language tabs */}
          <div role="tablist" aria-label="Language" className="flex gap-2 mb-6 border-b">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'en'}
              onClick={() => setActiveTab('en')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'en'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              English (EN)
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'es'}
              onClick={() => setActiveTab('es')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'es'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Spanish (ES)
            </button>
          </div>

          {/* Program Overview */}
          <div className="mb-6">
            <label
              htmlFor="program-overview"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Program Overview
            </label>
            <textarea
              id="program-overview"
              value={localeContent?.programOverview ?? ''}
              onChange={(e) => updateStringField(activeTab, 'programOverview', e.target.value)}
              rows={4}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the NourishedRx ACCESS program…"
            />
          </div>

          {/* Supported Conditions */}
          <div className="mb-6">
            <label
              htmlFor="supported-conditions"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Supported Conditions (one per line)
            </label>
            <textarea
              id="supported-conditions"
              value={(localeContent?.supportedConditions ?? []).join('\n')}
              onChange={(e) =>
                updateStringArrayField(activeTab, 'supportedConditions', e.target.value)
              }
              rows={6}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Hypertension&#10;Prediabetes&#10;Obesity/overweight"
            />
          </div>

          {/* Benefits List */}
          <div className="mb-6">
            <label htmlFor="benefits-list" className="block text-sm font-medium text-gray-700 mb-1">
              Benefits List (one per line)
            </label>
            <textarea
              id="benefits-list"
              value={(localeContent?.benefitsList ?? []).join('\n')}
              onChange={(e) => updateStringArrayField(activeTab, 'benefitsList', e.target.value)}
              rows={5}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Betty AI nutrition coach — available 24/7&#10;Clinical nutrition guidance"
            />
          </div>

          {/* Enrollment Steps (JSON) */}
          <div className="mb-6">
            <label
              htmlFor="enrollment-steps"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Enrollment Steps (JSON array)
            </label>
            <textarea
              id="enrollment-steps"
              value={enrollmentStepsJson}
              onChange={(e) => handleEnrollmentStepsJson(activeTab, e.target.value)}
              rows={10}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={
                '[\n  { "stepNumber": 1, "title": "Ask your provider", "description": "..." }\n]'
              }
            />
            <p className="text-xs text-gray-500 mt-1">
              Array of {'{ stepNumber, title, description }'} objects.
            </p>
          </div>

          {/* FAQ Items (JSON) */}
          <div className="mb-6">
            <label htmlFor="faq-items" className="block text-sm font-medium text-gray-700 mb-1">
              FAQ Items (JSON array)
            </label>
            <textarea
              id="faq-items"
              value={faqItemsJson}
              onChange={(e) => handleFaqItemsJson(activeTab, e.target.value)}
              rows={10}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={'[\n  { "question": "Is there a cost?", "answer": "No cost..." }\n]'}
            />
            <p className="text-xs text-gray-500 mt-1">Array of {'{ question, answer }'} objects.</p>
          </div>

          {/* Contact Email */}
          <div className="mb-6">
            <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email
            </label>
            <input
              id="contact-email"
              type="email"
              value={localeContent?.contactEmail ?? ''}
              onChange={(e) => updateStringField(activeTab, 'contactEmail', e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="access@nourishedrx.com"
            />
          </div>

          {/* Provider Directory URL */}
          <div className="mb-6">
            <label
              htmlFor="provider-dir-url"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Provider Directory URL (optional)
            </label>
            <input
              id="provider-dir-url"
              type="url"
              value={localeContent?.providerDirectoryUrl ?? ''}
              onChange={(e) => updateProviderDirectoryUrl(activeTab, e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://providers.nourishedrx.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to show contact email instead of directory link.
            </p>
          </div>

          {/* JSON error */}
          {jsonError && (
            <p role="alert" className="text-red-600 text-sm mb-4">
              JSON Error: {jsonError}
            </p>
          )}

          {/* Status messages */}
          {saveError && (
            <p role="alert" className="text-red-600 text-sm mb-4">
              Error: {saveError}
            </p>
          )}
          {saveSuccess && (
            <p role="status" className="text-green-600 text-sm mb-4">
              Content published successfully.
            </p>
          )}

          {/* Publish button */}
          <button
            type="submit"
            disabled={isSaving || !!jsonError}
            className="rounded-lg bg-blue-600 px-6 py-2 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {isSaving ? 'Publishing…' : 'Publish'}
          </button>

          {content?.publishedAt && (
            <p className="mt-4 text-xs text-gray-400">
              Last published: {new Date(content.publishedAt).toLocaleString()}
              {content.publishedBy ? ` by ${content.publishedBy}` : ''}
            </p>
          )}
        </form>
      )}
    </main>
  );
}
